import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect, useRef, lazy, Suspense } from 'react'
import { HiBars3 } from 'react-icons/hi2'
import Sidebar from './components/Sidebar'
import MobileTabBar from './components/MobileTabBar'
import SplitLayout from './components/SplitView'
import ErrorBoundary from './components/ErrorBoundary'
import Tickets from './pages/Tickets'
import TicketDetail from './pages/TicketDetail'
import { Operators, Reports, Audit, Categories, Templates, FAQ, Settings } from './pages/OtherPages'
import AIMonitoring from './pages/AIMonitoring'
import ResourceGaps from './pages/ResourceGaps'
import Feedback from './pages/Feedback'
import ChannelBase from './pages/ChannelBase'
import { Bots, Profile } from './pages/BotsAndProfile'
import Kritik from './pages/Kritik'
import ServerManager from './pages/ServerManager'
import { UserBots } from './pages/UserBots'
import Login from './pages/Login'
import { useAuthStore, useStore } from './store'
import { Loader } from './ui'
import { authApi } from './api'

// Lazy yuklash — SplitView.jsx dagi React.lazy() bilan bir xil chunk'dan foydalanadi
// (statik import code-splitting'ni bekor qilib qo'ygan edi)
const Dashboard = lazy(() => import('./pages/Dashboard'))
const MapPage   = lazy(() => import('./pages/MapPage'))
const UIKit     = lazy(() => import('./pages/UIKit'))
const Users     = lazy(() => import('./pages/Users'))

// Marshrut yuklanayotganda ko'rinadigan belgi — "working" holati.
const lazyFallback = <Loader state="working" size="lg" block />

// Xarita alohida: u og'ir chunk va geometriya tayyorlaydi, shuning uchun
// boshqa belgi — operator nimani kutayotganini shakldan biladi.
const mapFallback = <Loader state="shaping" size="lg" block label="Xarita tayyorlanmoqda" />

function AuthGuard({ children }) {
  const { user } = useAuthStore()
  if (!user) return <Navigate to="/login" replace />
  return children
}

function AppShell({ children }) {
  const { theme, mobileNavOpen, setMobileNav } = useStore()
  const { user } = useAuthStore()
  const { pathname } = useLocation()

  // Sahifa almashdi — panel yopiladi. Usiz operator havolani bosgach panel
  // ochiq qolib, ochilgan sahifani to'sib turardi.
  useEffect(() => { setMobileNav(false) }, [pathname, setMobileNav])

  // Esc — chetdan chiqadigan panel uchun ham (kitdagi modal bilan bir xil odat)
  useEffect(() => {
    if (!mobileNavOpen) return
    const onKey = e => { if (e.key === 'Escape') setMobileNav(false) }
    document.addEventListener('keydown', onKey)
    // Panel ochiq turganda orqadagi sahifa surilmasin
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [mobileNavOpen, setMobileNav])

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
  }, [theme])

  // Heartbeat: 30s da online status yangilash
  useEffect(() => {
    if (!user) return
    const beat = async () => {
      try { await authApi.heartbeat() } catch {}
    }
    beat()
    const id = setInterval(beat, 30000)
    return () => clearInterval(id)
  }, [user])

  return (
    <div className={`app-shell ${mobileNavOpen ? 'nav-open' : ''}`}>
      {/* Faqat tor ekranda ko'rinadi (CSS). Yon panel u yerda oqimdan
          chiqadi, ya'ni logotip va menyuga kirish nuqtasi kerak bo'ladi. */}
      <header className="app-topbar">
        <button
          className="app-topbar__menu"
          onClick={() => setMobileNav(true)}
          aria-label="Menyuni ochish"
          aria-expanded={mobileNavOpen}
        >
          <HiBars3 size={18} aria-hidden="true" />
        </button>
        <span className="app-topbar__brand">Cyber Advice</span>
      </header>

      <Sidebar />

      {/* Ortdagi qoraytirish — kitning o'z qatlami (ui-modal-overlay), shuning
          uchun rangi ham, kirish animatsiyasi ham modal bilan bir xil. */}
      {mobileNavOpen && (
        <div
          className="ui-modal-overlay app-scrim"
          onClick={() => setMobileNav(false)}
          aria-hidden="true"
        />
      )}

      <div className="main-wrap">
        <main className="page-content"><SplitLayout>{children}</SplitLayout></main>
      </div>

      {/* Pastki qator — faqat telefonda o'zini chizadi. Yon panel ochilganda
          ham joyida qoladi: u sahifaning bir qismi emas, doimiy qatlam. */}
      <MobileTabBar />
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <Routes>
        <Route path="/login" element={<Login />} />
        {/* Dizayn katalogi — ataylab AuthGuard'dan tashqarida. Sahifa hech qanday
            API chaqirmaydi va faqat o'ylab topilgan namunaviy ma'lumot ko'rsatadi,
            shuning uchun uni ko'rish uchun backend ishlab turishi shart emas.
            Ilova qobig'i ham kerak emas: katalogning o'z navigatsiyasi va
            mavzu almashtirgichi bor. */}
        <Route path="/ui" element={<Suspense fallback={lazyFallback}><UIKit /></Suspense>} />
        <Route path="/*" element={
          <AuthGuard>
            <AppShell>
              <Routes>
                <Route path="/" element={<Suspense fallback={lazyFallback}><Dashboard /></Suspense>} />
                <Route path="/tickets" element={<Tickets />} />
                <Route path="/kritik" element={<Kritik />} />
                <Route path="/map" element={<Suspense fallback={mapFallback}><MapPage /></Suspense>} />
                <Route path="/ticket/:id" element={<TicketDetail />} />
                <Route path="/operators" element={<Operators />} />
                <Route path="/users" element={<Suspense fallback={lazyFallback}><Users /></Suspense>} />
                <Route path="/bots" element={<Bots />} />
                <Route path="/userbots" element={<UserBots />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/audit" element={<Audit />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/templates" element={<Templates />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/server" element={<ServerManager />} />
                <Route path="/ai-monitoring" element={<AIMonitoring />} />
                <Route path="/resource-gaps" element={<ResourceGaps />} />
                <Route path="/feedback" element={<Feedback />} />
                <Route path="/channel" element={<ChannelBase />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AppShell>
          </AuthGuard>
        } />
      </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  )
}

